import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { db, auth } from '../firebase';
import {
  collection, onSnapshot, addDoc, deleteDoc, doc, updateDoc,
  query, where, serverTimestamp,
} from 'firebase/firestore';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
} from 'firebase/auth';
import { trackPageView, trackWhatsApp, trackViewProduct, trackAddToCart, trackBeginCheckout, trackPurchase, trackSignUp } from '../utils/tracking';
import { primaryColor } from '../utils/colorDetect';
import { slugify, findBySlug, shopPath } from '../utils/slug';
import {
  addLine, changeLineQty, removeLine, cartCountOf, cartTotalOf,
  buildOrderMessage, orderItemsPayload,
} from '../utils/cart';

const StoreContext = createContext();

/* react-snap prerenders the site with Puppeteer using the user-agent "ReactSnap",
   and package.json has skipThirdPartyRequests:true — so every Firebase call is
   blocked during the build. Detect that and skip the live listeners instead of
   letting Firestore retry (and spam the build log) against a dead connection.
   The real browser still runs them normally after hydration. */
const isPrerender =
  typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap';

/** Subcategory a product belongs to, tolerating the older field name. */
export const productSubcat = (p) => (p && (p.subCat || p.necklaceCat)) || '';

export function StoreProvider({ children }) {
  const [page, setPage] = useState('home');
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(!isPrerender);
  const [selectedId, setSelectedId] = useState(null);
  const [prevPage, setPrevPage] = useState('shop');
  const [cart, setCart] = useState([]);
  const [nid, setNid] = useState(1);
  const [waNum, setWaNum] = useState('923117401000');
  const [adminPass, setAdminPass] = useState('dulhahouse2001');
  const [shopCat, setShopCat] = useState('All');
  const [shopSub, setShopSub] = useState('All');                 // subcategory filter
  const [shopNecklaceCat, setShopNecklaceCat] = useState('All'); // legacy alias, kept in sync
  const [shopSort, setShopSort] = useState('def');
  const [shopPriceMin, setShopPriceMin] = useState(null); // null = no min
  const [shopPriceMax, setShopPriceMax] = useState(null); // null = no limit
  const [cartOpen, setCartOpen] = useState(false);
  const [mobOpen, setMobOpen] = useState(false);
  const [lockOpen, setLockOpen] = useState(false);
  const [lastTap, setLastTap] = useState(0);

  /* Category hierarchy — all of it lives in Firestore */
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [catsLoading, setCatsLoading] = useState(!isPrerender);
  const [necklaceCats, setNecklaceCats] = useState([]); // legacy collection, still read

  const [currentUser, setCurrentUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [myOrders, setMyOrders] = useState([]);
  const [pendingCheckout, setPendingCheckout] = useState(false);
  const [selectedPostSlug, setSelectedPostSlug] = useState(null);

  /* A /shop/rings/gold-rings URL arrives before the category list has
     loaded from Firestore, so the slugs are parked here and resolved to
     real names by an effect once the data is in. */
  const [pendingShopSlugs, setPendingShopSlugs] = useState(null);

  const Rs = (n) => 'Rs. ' + Number(n).toLocaleString();

  /* ---- Real URL paths (SEO / AdSense friendly) ---- */
  const pathForPage = (pg, opts = {}) => {
    if (pg === 'home') return '/';
    if (pg === 'product') return '/product/' + encodeURIComponent(opts.productId || '');
    if (pg === 'blogpost') return '/blog/' + encodeURIComponent(opts.postSlug || '');
    // Category and subcategory are part of the URL, so every shelf in the
    // shop is a page that can be linked to, indexed and ranked on its own.
    if (pg === 'shop') return shopPath(opts.cat, opts.sub);
    return '/' + pg;
  };

  const parsePath = () => {
    let path = window.location.pathname || '/';
    // Legacy support: old links like /#shop or /#about
    const hash = (window.location.hash || '').replace('#', '');
    if ((path === '/' || path === '') && hash) path = '/' + hash;
    const parts = path.split('/').filter(Boolean);
    if (parts.length === 0) return { page: 'home' };
    if (parts[0] === 'product' && parts[1]) return { page: 'product', productId: decodeURIComponent(parts[1]) };
    if (parts[0] === 'blog' && parts[1]) return { page: 'blogpost', postSlug: decodeURIComponent(parts[1]) };
    if (parts[0] === 'shop') {
      return {
        page: 'shop',
        catSlug: parts[1] ? decodeURIComponent(parts[1]) : null,
        subSlug: parts[2] ? decodeURIComponent(parts[2]) : null,
      };
    }
    const known = ['home', 'shop', 'about', 'contact', 'account', 'admin', 'blog', 'privacy-policy', 'terms'];
    if (known.includes(parts[0])) return { page: parts[0] === 'home' ? 'home' : parts[0] };
    return { page: 'home' };
  };

  const navigate = (pg, opts = {}) => {
    setPage(pg);
    setMobOpen(false);
    setCartOpen(false);
    if (!opts.replace) {
      window.history.pushState(
        {
          page: pg,
          productId: opts.productId || null,
          postSlug: opts.postSlug || null,
          cat: opts.cat || null,
          sub: opts.sub || null,
        },
        '',
        pathForPage(pg, opts)
      );
    }
    if (opts.scroll !== false) window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goBack = () => {
    if (window.history.length > 1) window.history.back();
    else navigate('home', { replace: true });
  };

  useEffect(() => {
    // Read the real URL on first load (supports old #hash links too)
    const init = parsePath();
    if (init.productId) setSelectedId(init.productId);
    if (init.postSlug) setSelectedPostSlug(init.postSlug);
    if (init.catSlug) setPendingShopSlugs({ cat: init.catSlug, sub: init.subSlug });
    setPage(init.page);
    window.history.replaceState(
      { page: init.page, productId: init.productId || null, postSlug: init.postSlug || null },
      '',
      pathForPage(init.page, init)
    );

    const onPop = (e) => {
      const st = e.state && e.state.page ? e.state : parsePath();
      setMobOpen(false);
      setCartOpen(false);
      if (st.page === 'product' && st.productId) setSelectedId(st.productId);
      if (st.page === 'blogpost' && st.postSlug) setSelectedPostSlug(st.postSlug);
      if (st.page === 'shop') {
        if (st.cat) {
          setShopCat(st.cat);
          setShopSub(st.sub || 'All');
        } else if (st.catSlug) {
          setPendingShopSlugs({ cat: st.catSlug, sub: st.subSlug });
        } else {
          setShopCat('All');
          setShopSub('All');
        }
      }
      setPage(st.page || 'home');
      window.scrollTo({ top: 0, behavior: 'auto' });
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Fire a page_view on every SPA route change (Meta Pixel + GA4) */
  useEffect(() => {
    trackPageView(page);
  }, [page]);

  /* ---- Per-page document titles (SEO) ----
     Pages listed here (home, shop, about, contact, blog, privacy-policy,
     terms, blog posts, products) now set their own title/description/canonical
     via react-helmet-async directly in their components. Only pages WITHOUT
     their own Helmet tags fall back to this generic setter, so the two
     mechanisms never fight over document.title. */
  useEffect(() => {
    const base = 'City Shopping Center & Dulha House';
    const titles = {
      account: 'My Account — ' + base,
      admin: base,
    };
    if (titles[page]) document.title = titles[page];
  }, [page]);

  /* ---- Realtime product feed ---- */
  useEffect(() => {
    if (isPrerender) return;
    const unsub = onSnapshot(collection(db, 'products'), (snapshot) => {
      const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setProducts(data);
      setProductsLoading(false);
    }, (err) => {
      console.error('products listen error', err);
      setProductsLoading(false);
    });
    return () => unsub();
  }, []);

  /* ---- Realtime category feed ---- */
  useEffect(() => {
    if (isPrerender) return;
    const unsub = onSnapshot(collection(db, 'categories'), (snapshot) => {
      const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      data.sort((a, b) =>
        (a.order ?? 999) - (b.order ?? 999) ||
        (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0)
      );
      setCategories(data);
      setCatsLoading(false);
    }, (err) => {
      console.error('categories listen error', err);
      setCatsLoading(false);
    });
    return () => unsub();
  }, []);

  /* ---- Realtime subcategory feed ---- */
  useEffect(() => {
    if (isPrerender) return;
    const unsub = onSnapshot(collection(db, 'subcategories'), (snapshot) => {
      const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      data.sort((a, b) =>
        (a.order ?? 999) - (b.order ?? 999) ||
        (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0)
      );
      setSubcategories(data);
    }, (err) => { console.error('subcategories listen error', err); });
    return () => unsub();
  }, []);

  /* ---- Legacy necklace categories ----
     Kept alive so the categories the shop already created keep working.
     They surface automatically as subcategories of "Necklaces" below,
     which means no manual data migration is needed. */
  useEffect(() => {
    if (isPrerender) return;
    const unsub = onSnapshot(collection(db, 'necklaceCategories'), (snapshot) => {
      const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0));
      setNecklaceCats(data);
    }, () => { /* collection may not exist — that is fine */ });
    return () => unsub();
  }, []);

  /* ============================================================
     CATEGORY LIST
     Categories come from the `categories` collection. Until the
     admin has created any, the list is derived from the categories
     real products are already filed under — so the shop never goes
     blank, and no placeholder records get written to the database.
     ============================================================ */
  const catNames = useMemo(() => {
    // Saved categories first (in their saved order), then any category
    // that only exists on products. Merging the two means saving one
    // category to the database never hides the others — every category
    // that real products use stays visible on the home page and shop.
    const names = categories.map((c) => c.name);
    const lower = names.map((n) => n.toLowerCase());
    const derived = [];
    for (const p of products) {
      const c = (p.cat || '').trim();
      if (!c) continue;
      const cl = c.toLowerCase();
      if (lower.includes(cl)) continue;
      if (derived.some((d) => d.toLowerCase() === cl)) continue;
      derived.push(c);
    }
    derived.sort((a, b) => a.localeCompare(b));
    return [...names, ...derived];
  }, [categories, products]);

  /** Full category records (real ids only for DB-backed ones). */
  const categoryList = useMemo(() => {
    const lower = categories.map((c) => c.name.toLowerCase());
    const derived = catNames
      .filter((n) => !lower.includes(n.toLowerCase()))
      .map((name) => ({ id: 'derived:' + name, name, derived: true }));
    return [...categories, ...derived];
  }, [categories, catNames]);

  /**
   * Subcategories of a category. Records from the new collection come
   * first, then the legacy necklace categories, then any value products
   * already carry that has no record yet — so nothing is ever orphaned.
   */
  const subcatsFor = useCallback((catName) => {
    if (!catName || catName === 'All') return [];

    const cat = categories.find((c) => c.name === catName);
    const out = subcategories.filter(
      (s) => (cat && s.categoryId === cat.id) || s.categoryName === catName
    );

    const has = (n) => out.some((s) => s.name.toLowerCase() === String(n).toLowerCase());

    if (catName === 'Necklaces') {
      for (const legacy of necklaceCats) {
        if (!has(legacy.name)) out.push({ ...legacy, categoryName: 'Necklaces', legacy: true });
      }
    }

    for (const p of products) {
      if (p.cat !== catName) continue;
      const sub = productSubcat(p);
      if (sub && !has(sub)) {
        out.push({
          id: 'derived:' + catName + ':' + sub,
          name: sub,
          categoryName: catName,
          derived: true,
        });
      }
    }

    return out;
  }, [categories, subcategories, necklaceCats, products]);

  /* ============================================================
     CATEGORY MUTATIONS (admin)
     Each returns { ok, msg } so the UI can say exactly what happened.
     ============================================================ */

  const addCategory = async (name) => {
    const clean = (name || '').trim();
    if (!clean) return { ok: false, msg: 'Category name required' };
    if (categories.some((c) => c.name.toLowerCase() === clean.toLowerCase())) {
      return { ok: false, msg: 'That category already exists' };
    }
    try {
      await addDoc(collection(db, 'categories'), {
        name: clean,
        order: categories.length,
        createdAt: serverTimestamp(),
      });
      return { ok: true };
    } catch (err) {
      console.error('Add category error:', err);
      return { ok: false, msg: 'Could not save the category' };
    }
  };

  const updateCategory = async (id, name) => {
    const clean = (name || '').trim();
    if (!clean) return { ok: false, msg: 'Category name required' };
    const current = categories.find((c) => c.id === id);
    if (!current) return { ok: false, msg: 'Category not found' };
    if (categories.some((c) => c.id !== id && c.name.toLowerCase() === clean.toLowerCase())) {
      return { ok: false, msg: 'Another category already uses that name' };
    }
    try {
      await updateDoc(doc(db, 'categories', id), { name: clean, updatedAt: serverTimestamp() });

      // Keep the denormalised name on subcategories and products in step
      const subs = subcategories.filter((s) => s.categoryId === id);
      await Promise.all(subs.map((s) =>
        updateDoc(doc(db, 'subcategories', s.id), { categoryName: clean })
      ));

      const affected = products.filter((p) => p.cat === current.name);
      await Promise.all(affected.map((p) =>
        updateDoc(doc(db, 'products', p.id), { cat: clean, updatedAt: serverTimestamp() })
      ));

      return { ok: true, moved: affected.length };
    } catch (err) {
      console.error('Update category error:', err);
      return { ok: false, msg: 'Could not rename the category' };
    }
  };

  const deleteCategory = async (id) => {
    const cat = categories.find((c) => c.id === id);
    if (!cat) return { ok: false, msg: 'Category not found' };

    const inUse = products.filter((p) => p.cat === cat.name).length;
    if (inUse > 0) {
      return {
        ok: false,
        msg: `${inUse} product${inUse === 1 ? '' : 's'} still use this category. Move them first, then delete it.`,
      };
    }

    try {
      // Remove its subcategories too, otherwise they are orphaned
      const subs = subcategories.filter((s) => s.categoryId === id);
      await Promise.all(subs.map((s) => deleteDoc(doc(db, 'subcategories', s.id))));
      await deleteDoc(doc(db, 'categories', id));
      return { ok: true };
    } catch (err) {
      console.error('Delete category error:', err);
      return { ok: false, msg: 'Could not delete the category' };
    }
  };

  const addSubcategory = async (categoryId, name) => {
    const clean = (name || '').trim();
    if (!clean) return { ok: false, msg: 'Subcategory name required' };
    const cat = categories.find((c) => c.id === categoryId);
    if (!cat) return { ok: false, msg: 'Pick a category first' };

    const siblings = subcatsFor(cat.name);
    if (siblings.some((s) => s.name.toLowerCase() === clean.toLowerCase())) {
      return { ok: false, msg: 'That subcategory already exists here' };
    }

    try {
      await addDoc(collection(db, 'subcategories'), {
        name: clean,
        categoryId,
        categoryName: cat.name,
        order: siblings.length,
        createdAt: serverTimestamp(),
      });
      return { ok: true };
    } catch (err) {
      console.error('Add subcategory error:', err);
      return { ok: false, msg: 'Could not save the subcategory' };
    }
  };

  /**
   * Add a subcategory by category *name*. If the category only exists as
   * a name on products (derived, never saved), it is saved to the
   * database first — so the admin can pick any existing category from a
   * list and add subcategories to it without extra steps.
   */
  const addSubcategoryToCat = async (catName, subName) => {
    const cleanCat = (catName || '').trim();
    const clean = (subName || '').trim();
    if (!cleanCat) return { ok: false, msg: 'Pick a category first' };
    if (!clean) return { ok: false, msg: 'Subcategory name required' };

    const siblings = subcatsFor(cleanCat);
    if (siblings.some((s) => s.name.toLowerCase() === clean.toLowerCase())) {
      return { ok: false, msg: 'That subcategory already exists here' };
    }

    try {
      const cat = categories.find(
        (c) => c.name.toLowerCase() === cleanCat.toLowerCase()
      );
      let categoryId = cat ? cat.id : null;
      const categoryName = cat ? cat.name : cleanCat;
      if (!categoryId) {
        // Category only exists on products so far — save it for real.
        const ref = await addDoc(collection(db, 'categories'), {
          name: categoryName,
          order: categories.length,
          createdAt: serverTimestamp(),
        });
        categoryId = ref.id;
      }
      await addDoc(collection(db, 'subcategories'), {
        name: clean,
        categoryId,
        categoryName,
        order: siblings.length,
        createdAt: serverTimestamp(),
      });
      return { ok: true, categoryName };
    } catch (err) {
      console.error('Add subcategory error:', err);
      return { ok: false, msg: 'Could not save the subcategory' };
    }
  };

  const updateSubcategory = async (id, name) => {
    const clean = (name || '').trim();
    if (!clean) return { ok: false, msg: 'Subcategory name required' };
    const current = subcategories.find((s) => s.id === id);
    if (!current) return { ok: false, msg: 'Subcategory not found' };

    const siblings = subcatsFor(current.categoryName).filter((s) => s.id !== id);
    if (siblings.some((s) => s.name.toLowerCase() === clean.toLowerCase())) {
      return { ok: false, msg: 'Another subcategory here already uses that name' };
    }

    try {
      await updateDoc(doc(db, 'subcategories', id), { name: clean, updatedAt: serverTimestamp() });

      const affected = products.filter(
        (p) => p.cat === current.categoryName && productSubcat(p) === current.name
      );
      await Promise.all(affected.map((p) =>
        updateDoc(doc(db, 'products', p.id), {
          subCat: clean,
          necklaceCat: p.cat === 'Necklaces' ? clean : (p.necklaceCat || ''),
          updatedAt: serverTimestamp(),
        })
      ));

      return { ok: true, moved: affected.length };
    } catch (err) {
      console.error('Update subcategory error:', err);
      return { ok: false, msg: 'Could not rename the subcategory' };
    }
  };

  const deleteSubcategory = async (id) => {
    const sub = subcategories.find((s) => s.id === id);
    if (!sub) return { ok: false, msg: 'Subcategory not found' };

    const inUse = products.filter(
      (p) => p.cat === sub.categoryName && productSubcat(p) === sub.name
    ).length;
    if (inUse > 0) {
      return {
        ok: false,
        msg: `${inUse} product${inUse === 1 ? '' : 's'} still use this subcategory. Move them first, then delete it.`,
      };
    }

    try {
      await deleteDoc(doc(db, 'subcategories', id));
      return { ok: true };
    } catch (err) {
      console.error('Delete subcategory error:', err);
      return { ok: false, msg: 'Could not delete the subcategory' };
    }
  };

  /**
   * One-time helper: copies the old `necklaceCategories` documents into
   * the new `subcategories` collection under Necklaces. Safe to run more
   * than once — it skips anything already there. Triggered by a button in
   * the admin panel, never automatically.
   */
  const importLegacyNecklaceCats = async () => {
    const cat = categories.find((c) => c.name === 'Necklaces');
    if (!cat) return { ok: false, msg: 'Create a "Necklaces" category first' };
    if (!necklaceCats.length) return { ok: false, msg: 'Nothing to import' };

    let added = 0;
    try {
      const existing = subcategories.filter((s) => s.categoryId === cat.id);
      for (const legacy of necklaceCats) {
        if (existing.some((s) => s.name.toLowerCase() === legacy.name.toLowerCase())) continue;
        await addDoc(collection(db, 'subcategories'), {
          name: legacy.name,
          categoryId: cat.id,
          categoryName: cat.name,
          order: existing.length + added,
          createdAt: serverTimestamp(),
        });
        added++;
      }
      return { ok: true, added };
    } catch (err) {
      console.error('Import legacy categories error:', err);
      return { ok: false, msg: 'Import failed' };
    }
  };

  /* ---- Legacy necklace category mutations (unchanged) ---- */
  const addNecklaceCat = async (name) => {
    const clean = (name || '').trim();
    if (!clean) return { ok: false, msg: 'Category name required' };
    const exists = necklaceCats.some((c) => c.name.toLowerCase() === clean.toLowerCase());
    if (exists) return { ok: false, msg: 'This category already exists' };
    try {
      await addDoc(collection(db, 'necklaceCategories'), {
        name: clean,
        createdAt: serverTimestamp(),
      });
      return { ok: true };
    } catch (err) {
      console.error('Add necklace category error:', err);
      return { ok: false, msg: 'Failed to save category' };
    }
  };

  const deleteNecklaceCat = async (id) => {
    try {
      await deleteDoc(doc(db, 'necklaceCategories', id));
      return { ok: true };
    } catch (err) {
      console.error('Delete necklace category error:', err);
      return { ok: false, msg: 'Failed to delete category' };
    }
  };

  /* ---- Auth ---- */
  useEffect(() => {
    if (isPrerender) { setAuthReady(true); return; }
    const unsub = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user || null);
      setAuthReady(true);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (isPrerender) return;
    if (!currentUser) { setMyOrders([]); return; }
    const q = query(collection(db, 'orders'), where('uid', '==', currentUser.uid));
    const unsub = onSnapshot(q, (snapshot) => {
      const list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setMyOrders(list);
    }, (err) => { console.error('orders listen error', err); });
    return () => unsub();
  }, [currentUser]);

  const signup = async (name, email, password) => {
    const cred = await createUserWithEmailAndPassword(auth, email.trim(), password);
    trackSignUp('email');
    if (name?.trim()) {
      try { await updateProfile(cred.user, { displayName: name.trim() }); } catch (_) {}
    }
    return cred.user;
  };

  const login = async (email, password) => {
    const cred = await signInWithEmailAndPassword(auth, email.trim(), password);
    return cred.user;
  };

  const logout = async () => { await signOut(auth); navigate('home'); };

  /* ---- Product navigation ---- */
  const viewProduct = (id) => {
    trackViewProduct(products.find((x) => x.id === id));
    setSelectedId(id);
    setPrevPage(page === 'product' ? prevPage : page);
    setPage('product');
    setMobOpen(false);
    setCartOpen(false);
    window.history.pushState(
      { page: 'product', productId: id, postSlug: null },
      '',
      pathForPage('product', { productId: id })
    );
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const viewBlogPost = (slug) => {
    setSelectedPostSlug(slug);
    navigate('blogpost', { postSlug: slug });
  };

  const backFromProduct = () => { goBack(); };
  const selectedProduct = products.find((p) => p.id === selectedId) || null;

  const relatedProducts = (prod, limit = 4) => {
    if (!prod) return [];
    const sub = productSubcat(prod);
    // Closest first: same subcategory, then same category, then the rest.
    const sameSub = sub
      ? products.filter((p) => p.id !== prod.id && p.cat === prod.cat && productSubcat(p) === sub)
      : [];
    const sameCat = products.filter(
      (p) => p.id !== prod.id && p.cat === prod.cat && !sameSub.some((s) => s.id === p.id)
    );
    const others = products.filter((p) => p.id !== prod.id && p.cat !== prod.cat);
    return [...sameSub, ...sameCat, ...others].slice(0, limit);
  };

  const clearPriceFilter = () => {
    setShopPriceMin(null);
    setShopPriceMax(null);
  };

  /** Jump to the shop, optionally landing on a category / subcategory. */
  const goShop = (cat = 'All', sub = 'All') => {
    setShopCat(cat);
    setShopSub(sub);
    setShopNecklaceCat(cat === 'Necklaces' ? sub : 'All');
    clearPriceFilter();
    navigate('shop', { cat, sub });
  };

  /** Select a subcategory within the category currently being browsed. */
  const selectSubcat = (sub) => {
    setShopSub(sub);
    if (shopCat === 'Necklaces') setShopNecklaceCat(sub);
    // Give the shelf its own URL so it can be shared and indexed
    navigate('shop', { cat: shopCat, sub, scroll: false });
  };

  /* Resolve a /shop/rings/gold-rings URL once the category data exists.
     Runs at most once per navigation: pendingShopSlugs is cleared as soon
     as the names are known, or as soon as it is clear they never will be. */
  useEffect(() => {
    if (!pendingShopSlugs) return;
    if (!catNames.length) return;   // still loading — try again next render

    const cat = findBySlug(catNames, pendingShopSlugs.cat);
    if (!cat) {
      // Unknown category in the URL: fall back to the full shop rather
      // than showing an empty page.
      setShopCat('All');
      setShopSub('All');
      setPendingShopSlugs(null);
      return;
    }

    setShopCat(cat);

    const sub = pendingShopSlugs.sub
      ? findBySlug(subcatsFor(cat), pendingShopSlugs.sub)
      : null;
    setShopSub(sub || 'All');
    if (cat === 'Necklaces') setShopNecklaceCat(sub || 'All');

    setPendingShopSlugs(null);
  }, [pendingShopSlugs, catNames, subcatsFor]);

  const openWA = () => {
    trackWhatsApp('floating_button');
    window.open(
      'https://wa.me/' + waNum + '?text=' + encodeURIComponent('Assalam o Alaikum! Mujhe aap ke jewelry collection ke baare mein jaanna hai.'),
      '_blank'
    );
  };

  const secretClick = () => {
    const now = Date.now();
    if (now - lastTap < 450) { setLockOpen(true); setLastTap(0); }
    else setLastTap(now);
  };

  /* ============================================================
     CART
     Lines are keyed by product + colour, so the same piece in two
     colours stays separate all the way to the order message.
     ============================================================ */

  /**
   * @param {string} id product id
   * @param {object} [opts]
   * @param {{name:string,hex:string}} [opts.color] chosen colour; falls back
   *        to the colour detected from the product photo
   * @param {string} [opts.size] chosen size, if the product has one
   */
  const addToCart = (id, opts = {}) => {
    const p = products.find((x) => x.id === id);
    if (!p) return;
    trackAddToCart(p);

    // Falls back to the colour detected from the photo when the shopper
    // has not picked one, so an order is never colourless.
    const color = opts.color || primaryColor(p);
    const size = opts.size || p.dimensions || '';

    setCart((prev) => addLine(prev, p, { color, size, img: opts.img || '' }));
    setCartOpen(true);
  };

  /* changeQty / removeItem accept either a line key or a bare product id,
     so any older call site keeps working. */
  const changeQty = (ref, delta) => setCart((prev) => changeLineQty(prev, ref, delta));
  const removeItem = (ref) => setCart((prev) => removeLine(prev, ref));

  const cartCount = cartCountOf(cart);
  const cartTotal = cartTotalOf(cart);

  const checkoutWA = async () => {
    if (!cart.length) return;
    if (!currentUser) {
      setPendingCheckout(true);
      setCartOpen(false);
      navigate('account');
      return;
    }

    trackBeginCheckout(cart, cartTotal);

    const customer = currentUser.displayName || currentUser.email;
    const msg = buildOrderMessage(cart, { name: customer, email: currentUser.email }, Rs);

    try {
      await addDoc(collection(db, 'orders'), {
        uid: currentUser.uid,
        customerName: customer,
        customerEmail: currentUser.email,
        items: orderItemsPayload(cart),
        total: cartTotal,
        status: 'placed',
        createdAt: serverTimestamp(),
      });
    } catch (err) { console.error('Could not save order:', err); }

    trackPurchase(cart, cartTotal);
    trackWhatsApp('checkout');

    window.open('https://wa.me/' + waNum + '?text=' + encodeURIComponent(msg), '_blank');
    setCart([]);
    setCartOpen(false);
    navigate('account');
  };

  const resumeCheckoutIfPending = () => {
    if (pendingCheckout) {
      setPendingCheckout(false);
      setTimeout(() => { checkoutWA(); }, 150);
      return true;
    }
    return false;
  };

  /* ---- Local product mutations (Firebase is source of truth via onSnapshot) ---- */
  const addProduct = (prod) => {
    setProducts((prev) => [{ ...prod, id: nid }, ...prev]);
    setNid((n) => n + 1);
  };

  const deleteProduct = (id) => setProducts((prev) => prev.filter((p) => p.id !== id));

  const updateProduct = (id, data) => {
    setProducts((prev) => prev.map((p) => p.id === id ? { ...p, ...data } : p));
  };

  const catCount = (cat) => products.filter((p) => p.cat === cat).length;
  const subcatCount = (cat, sub) =>
    products.filter((p) => p.cat === cat && productSubcat(p) === sub).length;

  /* ---- Shop filtering ---- */
  const getShopList = () => {
    let list = products.filter((p) => {
      if (shopCat !== 'All' && p.cat !== shopCat) return false;
      if (shopCat !== 'All' && shopSub !== 'All' && productSubcat(p) !== shopSub) return false;
      if (shopPriceMin !== null && p.price < shopPriceMin) return false;
      if (shopPriceMax !== null && p.price >= shopPriceMax) return false;
      return true;
    });
    if (shopSort === 'pa') list = [...list].sort((a, b) => a.price - b.price);
    else if (shopSort === 'pd') list = [...list].sort((a, b) => b.price - a.price);
    else if (shopSort === 'az') list = [...list].sort((a, b) => a.name.localeCompare(b.name));
    return list;
  };

  /* First real product photo in a category — used for the category tiles. */
  const categoryImage = useCallback((catName, subName) => {
    const match = products.find((p) => {
      if ((p.cat || '').toLowerCase() !== String(catName).toLowerCase()) return false;
      if (subName && productSubcat(p).toLowerCase() !== String(subName).toLowerCase()) return false;
      return Boolean(p.images?.[0] || p.img);
    });
    return match ? (match.images?.[0] || match.img) : null;
  }, [products]);

  return (
    <StoreContext.Provider value={{
      page, navigate, goShop, goBack,
      products, productsLoading, addProduct, deleteProduct, updateProduct,
      selectedProduct, viewProduct, backFromProduct, relatedProducts,
      selectedPostSlug, viewBlogPost,
      cart, cartCount, cartTotal, addToCart, changeQty, removeItem, checkoutWA,
      currentUser, authReady, myOrders,
      signup, login, logout,
      pendingCheckout, resumeCheckoutIfPending,

      /* shop filters */
      shopCat, setShopCat, shopSub, setShopSub, selectSubcat,
      shopSort, setShopSort,
      shopPriceMin, setShopPriceMin, shopPriceMax, setShopPriceMax, clearPriceFilter,
      getShopList,

      /* category hierarchy */
      categories, categoryList, catNames, subcategories, subcatsFor, catsLoading,
      addCategory, updateCategory, deleteCategory,
      addSubcategory, updateSubcategory, deleteSubcategory, addSubcategoryToCat,
      importLegacyNecklaceCats,
      categoryImage, catCount, subcatCount,

      /* legacy — kept so existing data and call sites keep working */
      shopNecklaceCat, setShopNecklaceCat,
      necklaceCats, addNecklaceCat, deleteNecklaceCat,

      cartOpen, setCartOpen,
      mobOpen, setMobOpen,
      lockOpen, setLockOpen,
      waNum, setWaNum,
      adminPass, setAdminPass,
      /* URL builders — components use these to render real <a href> links */
      pathForPage, shopPath, slugify,

      openWA, secretClick, Rs,
    }}>
      {children}
    </StoreContext.Provider>
  );
}

export const useStore = () => useContext(StoreContext);
